import 'App.css';
import React from 'react';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import { POST } from 'api/api';
import Lotto from "components/lotto/Lotto.js";
import MobileBottomNav from 'components/layout/MobileBottomNav';
import 'css/style.css';
import 'css/tokens.css';
import Logo from 'assets/logo_full.png';
import PlaceholderImage from 'assets/logo_full.png';
import { LazyLoadImage } from "react-lazy-load-image-component";

import { useParams } from "react-router-dom";
import { useEffect, useState } from 'react';

import {
  prepareTxtTitle,
  prepareTxtDescription,
  prepareTxtCanonicalUrl,
  prepareTxtKeywords,
} from 'components/lotto/utility/Utility.js';

import {
  getSearchMode,
  buildPageDescription,
  DEFAULT_OG_IMAGE,
} from 'seo/seoConfig';
import StructuredData from 'seo/StructuredData';
import SeoBreadcrumbs from 'components/seo/SeoBreadcrumbs';
import { AI_META_TAGS, AI_LINK_TAGS, buildAiCitationMeta } from 'seo/aiMeta';
import { GOOGLE_META_TAGS, GOOGLE_ROBOTS_CONTENT } from 'seo/googleSeo';
import { recordVisit, addRecentSearch } from 'storage/userStorage';
import { captureReferralFromUrl } from 'viral/viralShare';
import { Helmet } from "react-helmet";

export default function Main() {
  const [configData, setConfigData] = useState(null);
  const [visitInfo, setVisitInfo] = useState(null);
  const { keyword } = useParams();
  const mode = getSearchMode(keyword);
  const pageTitle = prepareTxtTitle(mode, keyword);
  const pageDescription = prepareTxtDescription(mode, keyword);
  const canonicalUrl = prepareTxtCanonicalUrl(mode, keyword);

  useEffect(() => {
    setVisitInfo(recordVisit());
    captureReferralFromUrl(window.location.search);
  }, []);

  useEffect(() => {
    if (keyword) {
      addRecentSearch(keyword);
    }
  }, [keyword]);

  useEffect(() => {
    const getConfigData = async () => {
      try {
        const configData = await POST('/lotto/config', { urlParameter: keyword });
        setConfigData(configData);
      } catch (error) {
        console.log('/lotto/config --> error: ', JSON.stringify(error));
      }
    };
    getConfigData();
  }, [keyword]);

  return (
    <div className="hotto-app">
      <Helmet>
        <html lang="th" />
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={prepareTxtKeywords(mode, keyword)} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="th-th" href={canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta name="robots" content={GOOGLE_ROBOTS_CONTENT} />
        {GOOGLE_META_TAGS.map((tag) => (
          <meta key={tag.name} name={tag.name} content={tag.content} />
        ))}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="og:locale" content="th_TH" />
        <meta property="og:site_name" content="Hotto" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        {AI_META_TAGS.map((tag) => (
          <meta key={tag.name} name={tag.name} content={tag.content} />
        ))}
        {AI_LINK_TAGS.map((tag) => (
          <link key={`${tag.rel}-${tag.href}`} rel={tag.rel} href={tag.href} {...(tag.type ? { type: tag.type } : {})} {...(tag.title ? { title: tag.title } : {})} />
        ))}
        {buildAiCitationMeta(canonicalUrl).map((tag) =>
          tag.name ? (
            <meta key={tag.name} name={tag.name} content={tag.content} />
          ) : (
            <meta key={tag.property} property={tag.property} content={tag.content} />
          )
        )}
      </Helmet>

      <StructuredData mode={mode} keyword={keyword} />

      {keyword && <SeoBreadcrumbs mode={mode} keyword={keyword} />}

      <header className={`site-header${keyword ? ' site-header--compact' : ''}`}>
        <a href="/" className="logo-link" aria-label="กลับหน้าแรก Hotto">
          <LazyLoadImage
            src={Logo}
            alt="Hotto"
            placeholdersrc={PlaceholderImage}
            effect="blur"
            width={keyword ? 72 : 88}
            height={keyword ? 72 : 88}
          />
          <span className="logo-wordmark">hotto</span>
        </a>
        {!keyword && (
          <p className="site-tagline visually-hidden-focusable">
            {buildPageDescription('default')}
          </p>
        )}
        {keyword && mode === 'dream' && (
          <h1 className="result-page-heading">ผลลัพธ์สำหรับ &ldquo;{keyword}&rdquo;</h1>
        )}
        {keyword && mode === 'number' && (
          <h1 className="result-page-heading">สถิติเลข {keyword}</h1>
        )}
      </header>

      <div className="hotto-body">
        {configData ? (
          <Lotto
            isShowAtRender={showAtRender(configData)}
            keyword={keyword}
            visitInfo={visitInfo}
          />
        ) : (
          <div className="skeleton-results" role="status">
            <div className="skeleton-line skeleton-line--lg" />
            <div className="skeleton-line" />
            <div className="skeleton-line skeleton-line--sm" />
          </div>
        )}
      </div>

      <footer className="site-footer" aria-label="ข้อมูลเว็บไซต์">
        <p>Hotto — หาเลขเด็ดจากฝัน เลขคำชะโนด ปฏิทินจีน เช็คสถิติย้อนหลัง 32 ปี</p>
      </footer>

      <MobileBottomNav />
    </div>
  );
}

function showAtRender(configData) {
  if (configData) {
    return configData?.data?.find((c) => c.configKey === 'collapse.showAtRender')
      ?.configValue === 'true';
  }
  return false;
}
